import * as THREE from 'three';
import { createStudio } from './scene.js';
import { loadStudioTextures } from './textures.js';
import { createMaterials } from './materials.js';
import { buildSarangi } from './sarangi.js';
import { buildBow } from './bow.js';
import { SarangiAudio } from './audio.js';
import { createUI } from './ui.js';
import { createPlayController } from './play.js';

export async function boot() {
  const canvas = document.getElementById('stage');
  const studio = createStudio(canvas);
  const { scene, camera, controls, composer, reduced } = studio;

  const tex = await loadStudioTextures();
  const mats = createMaterials(tex);
  const sarangi = buildSarangi(mats);
  const bow = buildBow(mats);

  const cushion = new THREE.Mesh(new THREE.SphereGeometry(0.15, 36, 22), mats.velvet);
  cushion.scale.set(1.2, 0.16, 0.95);
  cushion.position.set(0.0, 0.018, 0.02);
  cushion.receiveShadow = true;
  cushion.castShadow = true;

  const instrument = new THREE.Group();
  instrument.add(cushion);
  instrument.add(sarangi);
  instrument.add(bow);
  instrument.rotation.x = -0.06;
  instrument.rotation.y = 0.48;
  instrument.position.y = 0.02;
  instrument.scale.setScalar(1.05);
  scene.add(instrument);

  const audio = new SarangiAudio();
  let play;
  const playApi = {
    selectString: (i) => play?.selectString(i),
    triggerSwara: (t) => play?.triggerSwara(t),
    releaseSwara: (t) => play?.releaseSwara(t),
    setSnapping: (v) => play?.setSnapping(v),
  };
  const ui = createUI({
    audio,
    onMode: (m) => play?.setMode(m),
    onReady: playApi,
  });

  play = createPlayController({
    camera,
    canvas,
    controls,
    sarangi,
    bow,
    audio,
    ui,
  });

  const lookTarget = new THREE.Vector3(0.72, 0.36, 1.08);
  const playCam = new THREE.Vector3(0.28, 0.36, 1.05);
  const lookAt = new THREE.Vector3(0.02, 0.3, 0);

  const origSetMode = play.setMode.bind(play);
  play.setMode = (mode) => {
    origSetMode(mode);
    const dest = mode === 'play' ? playCam : lookTarget;
    if (reduced) {
      camera.position.copy(dest);
      controls.target.copy(lookAt);
      return;
    }
    const start = camera.position.clone();
    const t0 = performance.now();
    const run = () => {
      const k = Math.min(1, (performance.now() - t0) / 700);
      const e = 1 - Math.pow(1 - k, 3);
      camera.position.lerpVectors(start, dest, e);
      controls.target.lerp(lookAt, e);
      if (k < 1) requestAnimationFrame(run);
    };
    run();
  };

  ui.reveal();

  function tick() {
    requestAnimationFrame(tick);
    if (play.mode === 'look' && !reduced && !controls.enabled) {
      instrument.rotation.y += 0.00045;
    }
    play.update();
    controls.update();
    composer.render();
  }
  tick();
}
